/**
 * AuthSubmitButton - the single primary action of an auth screen.
 *
 * Native `<button>` rather than `components/ui/button` for the same reason
 * `AuthField` avoids `ui/input`: the design system's sizing is not reachable
 * from the outside, and this control is 46px with its own gradient.
 *
 * `pending` both disables the button and marks it busy, so the in-flight request
 * cannot be submitted twice and assistive tech reports the wait.
 */
import { cn } from "../../lib/utils";
import { AUTH_REVEAL_SUBMIT, type AuthRevealStep, authRevealClass } from "./authReveal";

export type AuthSubmitButtonProps = Omit<
  React.ComponentPropsWithRef<"button">,
  "className" | "type"
> & {
  /** Request in flight: disables the button and sets `aria-busy`. */
  pending?: boolean;
  /** Position in the entrance stagger; defaults to the submit step. */
  revealStep?: AuthRevealStep;
  className?: string;
};

export function AuthSubmitButton({
  pending = false,
  revealStep = AUTH_REVEAL_SUBMIT,
  className,
  disabled,
  children,
  ...buttonProps
}: AuthSubmitButtonProps) {
  return (
    <button
      {...buttonProps}
      type="submit"
      disabled={disabled === true || pending}
      aria-busy={pending || undefined}
      className={cn("auth-submit", authRevealClass(revealStep), className)}
    >
      {children}
    </button>
  );
}

/** Low-emphasis text action, for link rows and "sign out instead" escapes. */
export function AuthLinkButton({
  className,
  ...buttonProps
}: Omit<React.ComponentPropsWithRef<"button">, "className" | "type"> & { className?: string }) {
  return <button {...buttonProps} type="button" className={cn("auth-link", className)} />;
}
